import React, { useState, useEffect, useContext } from "react";
import { AuthService } from "../../api/AuthService";
import { paymentTypeEnum } from "../../api/constants";
import { ObjectLiteral } from "../../interfacesProps/ObjectLiteralProps";
import { RBCardView } from "../../components/RBCardView";
import { Spacing, Colors, UIRelatedConstants, } from "../../theme/Theme";
import {
    Button,
    CircularProgress,
    createStyles,
    makeStyles,
    useTheme,
} from "@material-ui/core";
import { useLocation } from "react-router";
import { RBContext } from "../../components/RBContext";
import { RBAppbar } from "../../components/RBAppbar";
import { RBButton } from "../../components/RBButton";
import { LoadingIndicator } from "../../components/LoadingIndicator";
import { renderRBPayText } from "../../components/RBPayWordmark";
import { RBAlert } from "../../components/RBAlert";
import { CreditCard, AccountBalance } from "@material-ui/icons";
import { Typography } from "@material-ui/core";
import { rbPayrollOwnerService } from "../rbPayrollOwnerSettings/services/rbPayrollOwner.service";
import { RBPayrollStatusBadge } from "../rbPayrollOwnerSettings/components/RBPayrollStatusBadge";
import { RB_PAYROLL_STATUS_COPY } from "../rbPayrollOwnerSettings/constants/rbPayrollOwner.constants";
import { AccountInfoContext } from "../../components/AccountInfoContext";

interface ViewPaymentMethodsPageProps { }
interface LocationState {
    selectedLoc?: ObjectLiteral;
}

type PaymentMethodsState = {
    creditCards?: any[];
    bankAccounts?: any[];
    disabledPaymentMethods?: string[];
    paymentType?: string;
};

export const ViewPaymentMethods: React.FC<ViewPaymentMethodsPageProps> = ({
    ...props
}) => {
    const location = useLocation<LocationState>();
    const { selectedLoc } = location.state || {};
    const selectedLocId = selectedLoc?.id;
    const { primaryColor, secondaryColor } = useContext(RBContext);
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethodsState>(
        { creditCards: [], bankAccounts: [] }
    );
    const [isAlertShowing, setIsAlertShowing] = useState(false);
    const [alertInfo, setAlertInfo] = useState<any>({ title: "", message: "", buttons: [] });
    const [loading, setLoading] = useState(false);
    const [refresh, setRefresh] = useState(false);
    const [rbPayrollState, setRbPayrollState] = useState<any>(null);

    const { accountInformation, fetchPharmacy } = useContext(AccountInfoContext);
    
    useEffect(() => {
        if (!selectedLocId) {
            return;
        }
        if (!accountInformation) {
            return;
        }

        const fetchPaymentMethods = async () => {
            try {
                setRefresh(true);
                const accountInfo = accountInformation;
                const methods = await fetchPharmacy(selectedLocId);
                const [rbPayrollSetup, rbPayrollOptions] = await Promise.all([
                    rbPayrollOwnerService.getRBPayrollSetup({ account: accountInfo, location: methods }),
                    rbPayrollOwnerService.getPaymentOptions({ account: accountInfo, location: methods }),
                ]);
                setPaymentMethods(methods);
                setRbPayrollState({
                    setup: rbPayrollSetup,
                    selected: rbPayrollOptions.some((option) => option.selected),
                });
            } catch (error) {
                console.error("Error fetching payment methods:", error);

            } finally {
                setRefresh(false);
            }
        };

        fetchPaymentMethods();
    }, [selectedLocId, accountInformation]);


    const useStyles = makeStyles(() =>
        createStyles({
            root: {
                textTransform: "none",
                // color: Colors.black,
                "&:hover": {
                    color: Colors.themeBlue,
                    opacity: 1,
                },
                "&$selected": {
                    color: Colors.black,
                    fontWeight: "600",
                },
                "&:focus": {
                    color: Colors.black,
                },
            },

            button: {
                borderRadius: UIRelatedConstants.ButtonBorderRadius,
                borderWidth: 1,
                backgroundColor: "#FFF",
                "&:hover": {
                    backgroundColor: "#CDCDCD",
                },
            },
            // Custom radio button styles
            radioContainer: {
                position: 'relative',
                padding: '10px', // Adjust padding as needed
                cursor: 'pointer',
            },
            radioInput: {
                position: 'absolute',
                opacity: 0,
                cursor: 'pointer',
            },
            radioCheckmark: {
                position: 'absolute',
                left: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                width: '20px', // Adjust the size of the tick mark
                height: '20px', // Adjust the size of the tick mark
                border: '2px solid #000', // Set the tick mark color
                borderTop: 'none',
                borderRight: 'none',
                boxSizing: 'border-box',
            },
            radioChecked: {
                backgroundColor: '#000', // Set the tick mark color when checked
            },
        })
    );
    const classes = useStyles();

    const isTraditionalInvoiceDisabled = () => {
        const disabledPaymentMethods = paymentMethods?.disabledPaymentMethods || [];
        return disabledPaymentMethods.includes(paymentTypeEnum.invoice);
    };

    const hasTraditionalInvoiceFallback = () => {
        return paymentMethods?.paymentType === paymentTypeEnum.invoice;
    };

    const getUniqueCreditCards = (cards: any[] = []) =>
        Object.values(
            cards.reduce((acc: any, card: any) => {
                if (!acc[card.paymentCardId]) {
                    acc[card.paymentCardId] = card;
                }
                return acc;
            }, {})
        );

    const isRemovingLastPaymentMethod = ({
        removingCardId,
        removingBankId,
    }: {
        removingCardId?: string;
        removingBankId?: string;
    }) => {
        const remainingBanks = (paymentMethods?.bankAccounts || []).filter(
            (bank: any) => bank.id !== removingBankId,
        );
        const remainingUniqueCards = getUniqueCreditCards(
            (paymentMethods?.creditCards || []).filter(
                (card: any) => card.paymentCardId !== removingCardId,
            ),
        );

        return remainingBanks.length === 0 && remainingUniqueCards.length === 0;
    };

    const showCannotRemoveLastPaymentMethodAlert = () => {
        setAlertInfo({
            title: 'Unable to Remove Payment Method',
            message:
                'Traditional Invoice is disabled for this location. You cannot remove the last available payment method. Please add another payment method first or contact your Network Administrator.',
            buttons: [
                <RBButton
                    key={'confirm'}
                    onClick={() => setIsAlertShowing(false)}
                    buttonBackgroundColor={primaryColor}
                    color="primary"
                    text="OK"
                />,
            ],
        });
        setIsAlertShowing(true);
    };

    const removeCard = (paymentCardId?: string) => {
        if (
            isTraditionalInvoiceDisabled() &&
            !hasTraditionalInvoiceFallback() &&
            isRemovingLastPaymentMethod({ removingCardId: paymentCardId })
        ) {
            showCannotRemoveLastPaymentMethodAlert();
            return;
        }

        const cards = paymentMethods?.creditCards || [];
        const sameCards = cards.filter((c: any) => c.paymentCardId === paymentCardId);

        const isUsedInBoth = sameCards.some((c: any) => c.category === 'job') &&
            sameCards.some((c: any) => c.category === 'shift');
        const message = isUsedInBoth
        ? "This card is used in both shift and job. Are you sure you want to delete it?"
        : "Are you sure you want to remove this credit card?";
        setAlertInfo({
            title: "Confirm Removal",
            message:message,
            buttons: [
                <RBButton
                    key={"confirm"}
                    onClick={() => setIsAlertShowing(false)}
                    buttonBackgroundColor={primaryColor}
                    color="primary"
                    disabled={loading}
                    text="Cancel"
                />,
                <RBButton
                    key={"confirm"}
                    isLoading={loading} // <-- Show loader when loading is true
                    onClick={async () => {
                        setLoading(true);
                        try {
                            // Call your API to remove the card
                            await AuthService.removeCreditCard(selectedLoc?.id,paymentCardId);
                            setPaymentMethods((prev: any) => ({
                                ...prev,
                                creditCards: prev.creditCards.filter(
                                    (c: any) => c.paymentCardId !== paymentCardId
                                ),
                            }));
                            setAlertInfo({
                                title: "Success",
                                message: "Credit card removed successfully.",
                                buttons: [
                                    <RBButton
                                        key={"confirm"}
                                        onClick={() => setIsAlertShowing(false)}
                                        buttonBackgroundColor={primaryColor}
                                        color="primary"
                                        text="OK"
                                    />,
                                ],

                            });
                            setIsAlertShowing(true);
                            // Refresh the payment methods list or update state accordingly
                        } catch (error) {
                            setAlertInfo({
                                title: "Error",
                                message: "Failed to remove credit card. Please try again.",
                                buttons: [
                                    <RBButton
                                        key={"confirm"}
                                        onClick={() => setIsAlertShowing(false)}
                                        buttonBackgroundColor={primaryColor}
                                        color="primary"
                                        text="OK"
                                    />,
                                ],
                            });
                            setIsAlertShowing(true);
                        }
                        finally {
                            setLoading(false); // <-- Hide loader
                        }
                    }}
                    buttonBackgroundColor={primaryColor}
                    color="primary"
                    text="Remove"
                />

            ],
        });

        setIsAlertShowing(true);

    };

    const removeBank = (bankId: string,paymentMethodId?: string) => {
        if (
            isTraditionalInvoiceDisabled() &&
            !hasTraditionalInvoiceFallback() &&
            isRemovingLastPaymentMethod({ removingBankId: bankId })
        ) {
            showCannotRemoveLastPaymentMethodAlert();
            return;
        }

        setAlertInfo({
            title: "Confirm Removal",
            message: "Are you sure you want to remove this bank account?",
            buttons: [
                 <RBButton
                    key={"confirm"}
                    onClick={() => setIsAlertShowing(false)}
                    buttonBackgroundColor={primaryColor}
                    color="primary"
                    disabled={loading}
                    text="Cancel"
                />,
                 <RBButton
                    key={"confirm"}
                    isLoading={loading} // <-- Show loader when loading is true
                    onClick={async () => {
                        try {
                            // Call your API to remove the card
                             setLoading(true);
                            await AuthService.removeBankAccount(selectedLoc?.id,paymentMethodId);
                            setPaymentMethods((prev: any) => ({
                                ...prev,
                                bankAccounts: prev.bankAccounts.filter((b: any) => b.id !== bankId),
                            }));
                             setAlertInfo({
                                title: "Success",
                                message: "Bank account removed successfully.",
                                buttons: [
                                    <RBButton
                                        key={"confirm"}
                                        onClick={() => setIsAlertShowing(false)}
                                        buttonBackgroundColor={primaryColor}
                                        color="primary"
                                        text="OK"
                                    />,
                                ],
                            });
                            setIsAlertShowing(true);
                            // Refresh the payment methods list or update state accordingly
                        } catch (error) {
                             setAlertInfo({
                                title: "Error",
                                message: "Failed to remove bank account. Please try again.",
                                buttons: [
                                    <RBButton
                                        key={"confirm"}
                                        onClick={() => setIsAlertShowing(false)}
                                        buttonBackgroundColor={primaryColor}
                                        color="primary"
                                        text="OK"
                                    />,
                                ],
                            });
                            setIsAlertShowing(true);
                        }
                         finally {
                            setLoading(false); // <-- Hide loader
                        }
                    }}
                    buttonBackgroundColor={primaryColor}
                    color="primary"
                    text="Remove"
                />
               
            ],
        });
        setIsAlertShowing(true);
    };

    const renderMethods = () => {
        const defaultBank = paymentMethods?.bankAccounts?.find((acc: any) => acc.isDefault);
        const creditCards = paymentMethods?.creditCards || [];
        const uniqueCards = getUniqueCreditCards(creditCards);
        // "View Payment Methods" lists EVERY method on file, so RB Pay shows
        // whenever a saved setup exists (status off NOT_STARTED) — independent of the
        // location's CURRENT method. (Unlike the Payment Settings card, which shows
        // only the active method.) An abandoned-before-save setup stays NOT_STARTED
        // and so is hidden here too.
        const shouldRenderRBPayroll = Boolean(
            rbPayrollState?.setup?.approval?.status &&
                rbPayrollState.setup.approval.status !== "NOT_STARTED"
        );
        const rbPayrollCopy = shouldRenderRBPayroll
            ? RB_PAYROLL_STATUS_COPY[
                (rbPayrollState?.setup?.approval?.status || "NOT_STARTED") as keyof typeof RB_PAYROLL_STATUS_COPY
            ]
            : null;
        const rbPayUpdatedLabel = (() => {
            const value = rbPayrollState?.setup?.meta?.updatedAt;
            if (!value) return "";
            const parsed = new Date(value);
            return Number.isNaN(parsed.getTime()) ? "" : parsed.toLocaleDateString();
        })();
        return (
                refresh ? <LoadingIndicator /> :
            <RBCardView
                style={{
                    margin: Spacing.large,
                    padding: Spacing.large,
                    height: "100%",
                }}
            >
                <div style={{ padding: 16 }}>
                    {shouldRenderRBPayroll ? (
                        <>
                            <Typography
                                variant="h6"
                                style={{
                                    fontWeight: 600,
                                    marginBottom: 12,
                                    borderBottom: "1px solid #e0e0e0",
                                    paddingBottom: 4,
                                }}
                            >
                                {renderRBPayText("RB Pay")}
                            </Typography>

                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-start",
                                    backgroundColor: "#f9f9f9",
                                    borderRadius: 12,
                                    padding: 16,
                                    marginBottom: 24,
                                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                    gap: 16,
                                    flexWrap: "wrap",
                                }}
                            >
                                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                    <div style={{ display: "flex", alignItems: "center" }}>
                                        <AccountBalance style={{ color: theme.palette.primary.main, marginRight: 8 }} />
                                        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                                            {renderRBPayText("RB Pay")}
                                        </Typography>
                                    </div>

                                    <Typography variant="body2" style={{ color: Colors.darkGrey }}>
                                        {rbPayrollState?.setup?.approval?.rejectionReason || renderRBPayText(rbPayrollCopy?.description)}
                                    </Typography>

                                    {rbPayrollState?.setup?.directDeposit?.accountNumber ? (
                                        <Typography variant="body2">
                                            Account: XXXX XXXX {String(rbPayrollState.setup.directDeposit.accountNumber).slice(-4)}
                                        </Typography>
                                    ) : null}

                                    {rbPayUpdatedLabel ? (
                                        <Typography variant="body2">
                                            Updated: {rbPayUpdatedLabel}
                                        </Typography>
                                    ) : null}
                                </div>

                                <RBPayrollStatusBadge status={rbPayrollState?.setup?.approval?.status || "NOT_STARTED"} />
                            </div>
                        </>
                    ) : null}

                    {/* === Pre-Authorized Debit (Bank Accounts) Heading === */}
                    <Typography
                        variant="h6"
                        style={{
                            fontWeight: 600,
                            marginBottom: 12,
                            borderBottom: "1px solid #e0e0e0",
                            paddingBottom: 4,
                        }}
                    >
                        Pre-Authorized Debit (Bank Accounts)
                    </Typography>

                    {defaultBank ? (
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                backgroundColor: "#f9f9f9",
                                borderRadius: 12,
                                padding: 16,
                                marginBottom: 24,
                                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                            }}
                        >
                            {/* Left section: Bank details */}
                            <div style={{ display: "flex", flexDirection: "column" }}>
                                <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                                    <AccountBalance style={{ color: theme.palette.primary.main, marginRight: 8 }} />
                                    <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                                        Pre-Authorized Debit (PAD)
                                    </Typography>
                                </div>
                                <Typography variant="body2">Bank: {defaultBank.bankName}</Typography>
                                <Typography variant="body2">
                                    Acc No: XXXX XXXX {defaultBank.accountNumberLastFour}
                                </Typography>
                            </div>

                            {/* Right section: Remove button */}
                            <Button
                                className={classes.button}
                                variant="outlined"
                                style={{
                                    borderColor: secondaryColor,
                                    color: Colors.white,
                                    textTransform: "none",
                                    background: secondaryColor,
                                    height: 36,
                                    alignSelf: "center",
                                }}
                                onClick={() => removeBank(defaultBank.id,defaultBank.paymentMethodId)}
                            >
                                Remove
                            </Button>
                        </div>
                    ) : (
                        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 24 }}>
                            No bank account found.
                        </Typography>
                    )}

                    {/* === Credit Cards Heading === */}
                    <Typography
                        variant="h6"
                        style={{
                            fontWeight: 600,
                            marginBottom: 12,
                            borderBottom: "1px solid #e0e0e0",
                            paddingBottom: 4,
                        }}
                    >
                        Credit Cards
                    </Typography>

                    {uniqueCards.length > 0 ? (
                        uniqueCards.map((card: any, index: any) => (
                            <div
                                key={card.id}
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-start",
                                    backgroundColor: "#f9f9f9",
                                    borderRadius: 12,
                                    padding: 16,
                                    marginBottom: 12,
                                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                }}
                            >
                                {/* Left section: Card details */}
                                <div>
                                    <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                                        <CreditCard style={{ color: theme.palette.primary.main, marginRight: 8 }} />
                                        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                                            Credit Card
                                        </Typography>
                                    </div>

                                    <Typography variant="body2">
                                        {card.type.toUpperCase()} ending with {card.lastFourDigits}
                                    </Typography>
                                    <Typography variant="body2">
                                        Expires: {card.expiryMonth}/{card.expiryYear}
                                    </Typography>
                                </div>

                                {/* Right section: Remove button */}
                                <Button
                                    className={classes.button}
                                    variant="outlined"
                                    style={{
                                        borderColor: secondaryColor,
                                        color: Colors.white,
                                        textTransform: "none",
                                        background: secondaryColor,
                                        height: 36,
                                        alignSelf: "center",
                                    }}
                                    onClick={() => removeCard(card.paymentCardId)}
                                >
                                    Remove
                                </Button>
                            </div>
                        ))
                    ) : (
                        <Typography variant="body2" color="textSecondary">
                            No credit cards found.
                        </Typography>
                    )}
                </div>
            </RBCardView>
        );

    }


    const renderTabHeader = () => {
        return (
            <RBAppbar
                deeperLevel={true}
                barName={"Location's Payment Methods"}
            />
        );
    };

    const theme = useTheme();
    return (
        <>
            {loading && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0, 0, 0, 0.4)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 2000,
                    }}
                >

                    <CircularProgress size={40} style={{ color: primaryColor }} />
                </div>
            )}

            <div>
                <RBAlert
                    show={isAlertShowing}
                    alertTitle={alertInfo.title}
                    alertMessage={alertInfo.message}
                    buttons={alertInfo.buttons}
                />
                {renderTabHeader()}
                {renderMethods()}

            </div>
        </>
    );
}

